/**
 * ConversationDetailPage — /conversations/:id. Built from the NextAdmin demo `/chats` on the `ChatPage` template:
 * the conversation list on the left, the thread in the middle, the context rail on the right; the header row holds the
 * title, status, who is viewing and the actions. The thread is the assistant-ui `ConversationThread` through
 * `ConversationChat`, over this page's one stream (presence is read off it).
 */
import { AlertTriangle, ArrowLeft, Bot } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { ChatPage } from '../../components/page/ChatPage.js';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import {
  ContextPanel,
  ContextPanelBreadcrumb,
  useCloseSummary,
  useContextPanel,
  useThreadSwap,
} from '../../components/context-panel/index.js';
import { ConversationChat } from '../../components/conversations/ConversationChat.js';
import { useConversationDetail } from '../../components/conversations/useConversationDetail.js';
import { ActionMenu, type ActionMenuItem } from '../../components/ui/ActionMenu.js';
import { Avatar } from '../../components/ui/Avatar.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Callout } from '../../components/ui/Callout.js';
import { Chip } from '../../components/ui/Chip.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { PageTitle } from '../../components/ui/PageTitle.js';
import { RuntimePolicyChip } from '../../components/ui/RuntimePolicyChip.js';
import { Sheet } from '../../components/ui/Sheet.js';
import { Skeleton } from '../../components/ui/Skeleton.js';
import { useConversationStream } from '../../hooks/useConversationStream.js';
import { useMediaQuery } from '../../hooks/useMediaQuery.js';
import { useProject } from '../../hooks/useProjects.js';
import { ConversationContextPanel } from './conversations-detail/ConversationContextPanel.js';
import { EntityOpenerProvider } from '../../components/conversations/entities/EntityOpenerProvider.js';
import { OpenConversationContext } from '../../components/conversations/open-conversation-context.js';
import { ConversationThreadRail } from './conversations-detail/ConversationThreadRail.js';
import { KIND_LABEL, STATUS_LABEL, STATUS_VARIANT } from './conversations-list/conversation-labels.js';

const MAX_VISIBLE_VIEWERS = 4;

/** Who has the conversation open right now (live presence off the stream). */
function Viewers({ viewers }: { viewers: Array<{ userId: string; displayName: string }> }) {
  if (viewers.length === 0) return null;
  const overflow = viewers.length - MAX_VISIBLE_VIEWERS;
  return (
    <div className="flex items-center gap-1" title={viewers.map((viewer) => viewer.displayName).join(', ')}>
      {viewers.slice(0, MAX_VISIBLE_VIEWERS).map((viewer) => (
        <Avatar key={viewer.userId} kind="human" label={viewer.displayName} size="sm" />
      ))}
      {overflow > 0 && <Badge variant="neutral">+{overflow}</Badge>}
    </div>
  );
}

function ThreadSkeleton() {
  return (
    <div className="flex h-full flex-col gap-5 p-6" aria-label="Loading conversation">
      {[0, 1, 2].map((row) => (
        <div key={row} className={row % 2 === 0 ? 'flex justify-start' : 'flex justify-end'}>
          <div className="w-3/5 space-y-2">
            <Skeleton height={12} width="40%" />
            <Skeleton height={10} width="85%" />
            <Skeleton height={10} width="60%" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ConversationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { panel, closePanel } = useContextPanel();
  const wide = useMediaQuery('(min-width: 1280px)');
  const [sheet, setSheet] = useState<'list' | 'details' | null>(null);

  // The conversation's one stream (query + socket + merge + poll): the thread renders from it and presence is read
  // off it — a second subscription here would be a second socket for the same conversation.
  const stream = useConversationStream(id ?? null, 'detailed');
  const detail = useConversationDetail(id ?? null, {
    awaitingAssistant: stream.awaitingAssistant,
    lastMessage: stream.messages.at(-1) ?? null,
  });
  const conv = detail.data;

  const projectId = conv?.projectId ?? searchParams.get('projectId');
  const { data: project } = useProject(projectId ?? undefined);

  const threadSwap = useThreadSwap(id ?? '', panel);
  const isEditorSwap = panel !== null && threadSwap.status !== 'parent';
  useCloseSummary({ parentConversationId: id ?? '' });

  const backTo = projectId ? `/projects/${projectId}?tab=conversations` : '/conversations';
  const backLabel = projectId ? `Back to ${project?.name ?? 'the project'}` : 'Back to conversations';
  const back = (
    <Link
      to={backTo}
      aria-label={backLabel}
      title={backLabel}
      className={buttonStyles({ variant: 'primary', appearance: 'outline', size: 'sm', iconOnly: true })}
    >
      <ArrowLeft size={16} aria-hidden />
    </Link>
  );

  if (detail.isLoading) {
    return <ChatPage header={back} thread={<ThreadSkeleton />} />;
  }

  if (detail.isError || !conv || !id) {
    return (
      <ChatPage
        header={back}
        thread={
          <div className="p-6">
            <Callout
              tone="danger"
              icon={AlertTriangle}
              title="Could not load this conversation"
              actions={
                <Button size="sm" variant="secondary" onClick={() => void detail.refetch()}>
                  Retry
                </Button>
              }
            >
              {detail.error?.message ?? 'It may have been removed, or you may not have access to it.'}
            </Callout>
          </div>
        }
      />
    );
  }

  const menuItems: ActionMenuItem[] = [
    ...(wide
      ? []
      : [
          { id: 'list', label: 'All conversations', onAction: () => setSheet('list') },
          { id: 'details', label: 'Conversation details', onAction: () => setSheet('details') },
        ]),
  ];

  const header = (
    <>
      <PageTitle
        title={conv.topic}
        backTo={backTo}
        backLabel={backLabel}
        badges={
          <>
            <Badge variant={STATUS_VARIANT[conv.status]} dot>
              {STATUS_LABEL[conv.status]}
            </Badge>
            <Chip tone="neutral">{KIND_LABEL[conv.kind]}</Chip>
            <RuntimePolicyChip policyId={conv.runtimePolicyId} />
          </>
        }
      />
      <div className="ml-auto flex items-center gap-2">
        <Viewers viewers={stream.eventsState.viewers} />
        {menuItems.length > 0 && <ActionMenu items={menuItems} aria-label="Conversation actions" />}
      </div>
    </>
  );

  const thread = (() => {
    if (!isEditorSwap)
      return <ConversationChat key={id} conversationId={id} surface="detailed" stream={stream} attachments />;
    if (threadSwap.status === 'pending') {
      return (
        <EmptyState icon={Bot} title="Opening the assistant" description="Setting up its conversation for this item." />
      );
    }
    if (threadSwap.status === 'error') {
      return (
        <div className="p-6">
          <Callout
            tone="danger"
            title="Could not open the assistant"
            actions={
              <Button size="sm" variant="secondary" onClick={closePanel}>
                Close panel
              </Button>
            }
          >
            {threadSwap.errorMessage}
          </Callout>
        </div>
      );
    }
    return (
      <div className="flex h-full min-h-0 flex-col">
        <div className="shrink-0 border-b px-4 py-3">
          <ContextPanelBreadcrumb parentLabel={conv.topic} onClick={closePanel} />
        </div>
        <div className="min-h-0 flex-1">
          <ConversationChat
            key={threadSwap.activeConvId}
            conversationId={threadSwap.activeConvId}
            surface="detailed"
            attachments
          />
        </div>
      </div>
    );
  })();

  const newestMessageAt = stream.messages.at(-1)?.createdAt ?? null;
  const rail = (
    <ConversationContextPanel
      conv={conv}
      conversationId={id}
      newestMessageAt={newestMessageAt}
      messages={stream.messages}
    />
  );

  return (
    // A record a card shows opens in the side panel over this page; a member's thread opens at its own route.
    <OpenConversationContext value={(threadId) => navigate(`/conversations/${threadId}`)}>
      <EntityOpenerProvider>
        <ContextPanel />
        <ChatPage
          header={header}
          list={<ConversationThreadRail activeConversationId={id} />}
          thread={thread}
          rail={rail}
        />
        <Sheet open={sheet === 'list'} onClose={() => setSheet(null)} title="Conversations" width="sm" side="left">
          <ConversationThreadRail activeConversationId={id} />
        </Sheet>
        <Sheet open={sheet === 'details'} onClose={() => setSheet(null)} title="Conversation details" width="md">
          {rail}
        </Sheet>
      </EntityOpenerProvider>
    </OpenConversationContext>
  );
}
